/**
 * Logique temporelle DÉTERMINISTE du Live School (live synchrone).
 *
 * La position dans le quiz est purement mathématique : aucun état serveur n'est
 * poussé au client (pas de WebSocket). Tout se calcule à partir de l'heure
 * serveur synchronisée et du `starts_at` figé du défi. Un élève qui se connecte
 * en retard est automatiquement propulsé à l'étape en cours (pas de marche
 * arrière). C'est le MIROIR exact de la formule de la spec :
 *
 *   S = floor((T_current − T_start) / D) + 1   (étape 1-indexée)
 *   R = D − ((T_current − T_start) mod D)       (temps restant dans l'étape)
 *
 * ⚠️ Toutes les durées sont en MILLISECONDES (le `step_duration_ms` de
 * `live_challenges`). Module pur — aucun accès à `Date.now()` (les tests
 * passent une horloge explicite ; le hook client passe `Date.now() + delta`).
 */

export type LiveQuizStatus = 'WAITING' | 'LIVE' | 'FINISHED';

export interface LiveQuizClock {
  /** Heure serveur synchronisée (ms epoch) = `Date.now() + serverDelta` côté client. */
  serverTime: number;
  /** Instant de départ figé du défi (ms epoch). */
  startsAt: number;
  /** Durée d'une étape (ms). Défaut produit : 30 000. */
  stepDurationMs: number;
  /** Nombre total d'étapes du défi. */
  stepCount: number;
  /** Durées par étape (défi privé, migration 0651) ; absent = durée fixe. */
  stepDurationsMs?: readonly number[] | null;
}

export interface LiveQuizState {
  status: LiveQuizStatus;
  /** Index 0-based de l'étape courante. `-1` tant que WAITING. Borné à stepCount-1 si FINISHED. */
  stepIndex: number;
  /** Étape 1-indexée (le `S` de la spec). `0` tant que WAITING, `stepCount` si FINISHED. */
  step: number;
  /** Temps restant (ms) : dans l'étape courante (LIVE), avant le départ (WAITING), 0 (FINISHED). */
  timeLeftMs: number;
  /** `serverTime − startsAt` (peut être négatif avant le départ). */
  elapsedMs: number;
}

/**
 * Calcule l'état complet du quiz à un instant serveur donné. Déterministe.
 *
 * - `elapsed < 0` → WAITING (le `timeLeftMs` est le compte à rebours jusqu'au départ).
 * - `stepIndex >= stepCount` → FINISHED.
 * - sinon → LIVE.
 */
export function getQuizState({
  serverTime,
  startsAt,
  stepDurationMs,
  stepCount,
  stepDurationsMs,
}: LiveQuizClock): LiveQuizState {
  const elapsedMs = serverTime - startsAt;

  if (elapsedMs < 0) {
    return { status: 'WAITING', stepIndex: -1, step: 0, timeLeftMs: -elapsedMs, elapsedMs };
  }

  if (stepDurationsMs && stepDurationsMs.length > 0) {
    let start = 0;
    for (let i = 0; i < stepCount; i++) {
      const d = stepDurationAt(i, stepDurationMs, stepDurationsMs);
      if (elapsedMs < start + d) {
        return {
          status: 'LIVE',
          stepIndex: i,
          step: i + 1,
          timeLeftMs: start + d - elapsedMs,
          elapsedMs,
        };
      }
      start += d;
    }
    return {
      status: 'FINISHED',
      stepIndex: Math.max(0, stepCount - 1),
      step: stepCount,
      timeLeftMs: 0,
      elapsedMs,
    };
  }

  const d = stepDurationMs > 0 ? stepDurationMs : 1;
  const rawIndex = Math.floor(elapsedMs / d);

  if (rawIndex >= stepCount) {
    return {
      status: 'FINISHED',
      stepIndex: Math.max(0, stepCount - 1),
      step: stepCount,
      timeLeftMs: 0,
      elapsedMs,
    };
  }

  return {
    status: 'LIVE',
    stepIndex: rawIndex,
    step: rawIndex + 1,
    timeLeftMs: d - (elapsedMs % d),
    elapsedMs,
  };
}

/**
 * Durée de l'étape `stepIndex` (ms). Défi privé (migration 0651) : chaque
 * question a sa durée (`step_durations_ms`, 2 min avec feuille de brouillon,
 * 45 s sinon) ; sans tableau, la durée fixe `stepDurationMs`.
 */
export function stepDurationAt(
  stepIndex: number,
  stepDurationMs: number,
  stepDurationsMs?: readonly number[] | null,
): number {
  const v = stepDurationsMs?.[stepIndex];
  if (typeof v === 'number' && v > 0) return v;
  return stepDurationMs > 0 ? stepDurationMs : 1;
}

/** Début de l'étape `stepIndex`, relatif au départ des questions (ms). Miroir SQL `live_step_offset_ms`. */
export function stepOffsetMs(
  stepIndex: number,
  stepDurationMs: number,
  stepDurationsMs?: readonly number[] | null,
): number {
  if (!stepDurationsMs || stepDurationsMs.length === 0) {
    return stepIndex * (stepDurationMs > 0 ? stepDurationMs : 1);
  }
  let start = 0;
  for (let i = 0; i < stepIndex; i++) start += stepDurationAt(i, stepDurationMs, stepDurationsMs);
  return start;
}

export interface ClockSyncSample {
  /** Heure serveur renvoyée par l'endpoint `live-state` (ms epoch). */
  serverTime: number;
  /** Heure de l'horloge cliente au moment de RECEVOIR la réponse (ms epoch). */
  clientReceiveTime: number;
  /** Estimation de la latence aller (≈ RTT/2, ms). Défaut 0. */
  latency?: number;
}

/**
 * Décalage horloge (ms) à AJOUTER à l'horloge cliente pour estimer l'heure
 * serveur : `syncedNow = Date.now() + delta`. Reprend la formule de la spec
 *   delta = serverTime − (clientReceiveTime − latency)
 * où `latency` (aller, ≈ RTT/2) corrige le temps de trajet retour de la réponse.
 */
export function computeClockDelta({
  serverTime,
  clientReceiveTime,
  latency = 0,
}: ClockSyncSample): number {
  return serverTime - (clientReceiveTime - latency);
}

/** Heure serveur estimée à partir de l'horloge cliente et du delta. Pur. */
export function syncedTime(clientNow: number, serverDelta: number): number {
  return clientNow + serverDelta;
}
